// Skill vectorization and similarity calculation

/**
 * Extract skill names from both legacy (string) and new (object) formats
 * @param {Array} skills - Array of skills (strings or objects)
 * @returns {Array} Array of skill names as strings
 */
export const extractSkillNames = (skills) => {
  if (!Array.isArray(skills)) return [];
  
  return skills.map(skill => {
    // Handle string format (legacy)
    if (typeof skill === 'string') {
      return skill;
    }
    
    // Handle object format (new up-skills format)
    if (typeof skill === 'object' && skill !== null) {
      return skill.name || skill.skill || 'Unknown Skill';
    }
    
    return 'Unknown Skill';
  }).filter(name => name && name !== 'Unknown Skill');
};

/**
 * Extract skill weights based on ratings (for enhanced matching)
 * @param {Array} skills - Array of skills (strings or objects)
 * @returns {Object} Object mapping skill names to weights
 */
export const extractSkillWeights = (skills) => {
  if (!Array.isArray(skills)) return {};
  
  const weights = {};
  
  skills.forEach(skill => {
    let skillName, weight;
    
    // Handle string format (legacy) - default weight
    if (typeof skill === 'string') {
      skillName = skill;
      weight = 1.0;
    }
    // Handle object format (new up-skills format)
    else if (typeof skill === 'object' && skill !== null) {
      skillName = skill.name || skill.skill;
      // Convert 1-5 rating to 0.6-1.0 weight (higher rating = higher weight)
      weight = skill.rating ? (0.6 + (skill.rating - 1) * 0.1) : 1.0;
    }
    
    if (skillName) {
      weights[skillName] = weight;
    }
  });
  
  return weights;
};

export const createSkillVector = (skills, allSkills) => {
  // Handle case where skills is undefined, null, or not an array
  const skillsArray = Array.isArray(skills) ? skills : [];
  const skillNames = extractSkillNames(skillsArray);
  return allSkills.map(skill => skillNames.includes(skill) ? 1 : 0);
};

/**
 * Create weighted skill vector using ratings from up-skills format
 * @param {Array} skills - Array of skills (strings or objects)
 * @param {Array} allSkills - Master list of all available skills
 * @returns {Array} Vector where each element is 0 or weighted value
 */
export const createWeightedSkillVector = (skills, allSkills) => {
  const skillsArray = Array.isArray(skills) ? skills : [];
  const skillNames = extractSkillNames(skillsArray);
  const skillWeights = extractSkillWeights(skillsArray);
  
  return allSkills.map(skill => {
    if (skillNames.includes(skill)) {
      return skillWeights[skill] || 1.0;
    }
    return 0;
  });
};

export const cosineSimilarity = (vecA, vecB) => {
  if (vecA.length !== vecB.length) return 0;
  
  const dotProduct = vecA.reduce((sum, a, i) => sum + a * vecB[i], 0);
  const magnitudeA = Math.sqrt(vecA.reduce((sum, a) => sum + a * a, 0));
  const magnitudeB = Math.sqrt(vecB.reduce((sum, b) => sum + b * b, 0));
  
  if (magnitudeA === 0 || magnitudeB === 0) return 0;
  
  return dotProduct / (magnitudeA * magnitudeB);
};

export const matchVAtoJobs = (vaSkills, jobs, allSkills) => {
  const vaVector = createSkillVector(vaSkills, allSkills);
  
  return jobs
    .map(job => {
      // Use correct field name from database schema: skills_required instead of requiredSkills
      const jobSkills = job.skills_required || job.requiredSkills || [];
      const jobVector = createSkillVector(jobSkills, allSkills);
      const score = cosineSimilarity(vaVector, jobVector);
      return { ...job, matchScore: parseFloat((score * 100).toFixed(1)) };
    })
    .sort((a, b) => b.matchScore - a.matchScore)
    .filter(job => job.matchScore > 30); // Only show jobs with >30% match
};
